import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../Context/AuthContext'
import { useCart } from '../Context/CartContext'
import { useCurrency } from '../Context/CurrencyContext'
import MainHeader from './MainHeader'
import HomeFooter from './HomeFooter'
import { CONTACT_INFO, SITE_LINKS } from '../constants/siteLinks'
import useTemplateScripts from '../hooks/useTemplateScripts'
import { createOrder } from '../services/orderService'
import {
  getPaymentMethods,
  initiatePayment,
  paymentErrorMessage,
  rememberPendingPayment,
  waitForPayment,
} from '../services/paymentService'
import './Checkout.css'

// Online payment methods (Centiwise). `id` is the method code the API expects.
const PAYMENT_OPTIONS = [
  {
    id: 'MTN',
    kind: 'momo',
    label: 'MTN MoMo',
    subtitle: 'Approve the payment on your MTN phone',
    logo: '/assets/img/brands/MTN-logo.jpg',
    popular: true,
  },
  {
    id: 'AIRTEL',
    kind: 'momo',
    label: 'Airtel Money',
    subtitle: 'Approve the payment on your Airtel phone',
    logo: '/assets/img/brands/Airtel-logo.png',
    logoContain: true,
  },
  {
    id: 'CARD',
    kind: 'card',
    label: 'Visa / Mastercard',
    subtitle: 'Pay on a secure card page',
    icon: 'fa-credit-card',
  },
]

const CHECKOUT_STEPS = [
  { id: 1, key: 'contact', label: 'Contact', icon: 'fa-user', hint: 'Confirm your contact details.' },
  { id: 2, key: 'delivery', label: 'Delivery', icon: 'fa-truck-fast', hint: 'Choose store pickup or home delivery.' },
  { id: 3, key: 'payment', label: 'Payment', icon: 'fa-credit-card', hint: 'Pay with MTN MoMo, Airtel Money or card.' },
  { id: 4, key: 'review', label: 'Review', icon: 'fa-clipboard-check', hint: 'Check everything, then place your order and pay.' },
]
const LAST_STEP = CHECKOUT_STEPS.length

const RWANDA_LOCATIONS = {
  'Kigali City': ['Gasabo', 'Kicukiro', 'Nyarugenge'],
  Northern: ['Burera', 'Gakenke', 'Gicumbi', 'Musanze', 'Rulindo'],
  Southern: ['Gisagara', 'Huye', 'Kamonyi', 'Muhanga', 'Nyamagabe', 'Nyanza', 'Nyaruguru', 'Ruhango'],
  Eastern: ['Bugesera', 'Gatsibo', 'Kayonza', 'Kirehe', 'Ngoma', 'Nyagatare', 'Rwamagana'],
  Western: ['Karongi', 'Ngororero', 'Nyabihu', 'Nyamasheke', 'Rubavu', 'Rusizi', 'Rutsiro'],
}

const RWANDA_PROVINCES = Object.keys(RWANDA_LOCATIONS)

const ALL_ZONE_DISTRICTS = Object.values(RWANDA_LOCATIONS).flat()

const STORE_PICKUP = {
  name: 'Fabritech Store',
  address: 'YYUSSA Plaza, Kisimenti, Remera, Kigali',
  phone: '+250 788 601 280',
}

const Checkout = () => {
  useTemplateScripts()
  const navigate = useNavigate()
  const { user, token, isAuthenticated, loading: authLoading } = useAuth()
  const { cartItems, clearLocalCart, syncCartForCheckout, getTotalPrice } = useCart()
  const {
    formatPrice,
    isForeign,
    currency,
    baseCurrency,
    setCurrency,
    currencies,
  } = useCurrency()

  const [step, setStep] = useState(1)
  const [deliveryMethod, setDeliveryMethod] = useState('store')
  const [paymentMethod, setPaymentMethod] = useState('MTN')
  // Mobile money number; null = same as the contact phone until the customer edits it
  const [payPhoneInput, setPayPhoneInput] = useState(null)
  const [acceptTerms, setAcceptTerms] = useState(true)
  const [showSuccess, setShowSuccess] = useState(false)
  const [toast, setToast] = useState(null)
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [pendingOrder, setPendingOrder] = useState(null)
  const [orderSnapshot, setOrderSnapshot] = useState([])
  // Methods the backend can take right now (null while loading = assume all)
  const [availableMethods, setAvailableMethods] = useState(null)
  const [cardRate, setCardRate] = useState(null) // RWF per 1 USD
  // Online payment progress: null | { phase: 'starting'|'redirecting'|'waiting'|'failed'|'timeout', payment?, error? }
  const [payState, setPayState] = useState(null)
  const pollRef = useRef(null)
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    deliveryDate: '',
    deliveryTime: '',
    country: 'Rwanda',
    province: '',
    district: '',
    sector: '',
    street: '',
  })

  const userId = user?.id || user?._id || null
  const selectedOption = PAYMENT_OPTIONS.find((o) => o.id === paymentMethod) || PAYMENT_OPTIONS[0]
  const payPhone = payPhoneInput ?? form.phone
  const isMethodAvailable = (id) => availableMethods === null || availableMethods.includes(id)

  // Which payment methods are configured on the server
  useEffect(() => {
    let cancelled = false
    getPaymentMethods()
      .then((data) => {
        if (cancelled) return
        const methods = (data.options || []).flatMap((option) =>
          option.currency === 'RWF' ? option.methods : option.methods.filter((m) => m === 'CARD')
        )
        setAvailableMethods(data.enabled ? methods : [])
        const card = (data.options || []).find((option) => option.methods.includes('CARD'))
        setCardRate(card?.rwfPerUnit || null)
      })
      .catch(() => {
        if (!cancelled) setAvailableMethods([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Default to the first available method
  useEffect(() => {
    if (availableMethods && availableMethods.length && !availableMethods.includes(paymentMethod)) {
      setPaymentMethod(PAYMENT_OPTIONS.find((o) => availableMethods.includes(o.id))?.id || paymentMethod)
    }
  }, [availableMethods, paymentMethod])

  // Stop polling when leaving the page
  useEffect(() => () => pollRef.current?.abort(), [])

  useEffect(() => {
    if (authLoading) return
    if (!isAuthenticated || !userId) {
      navigate('/login', {
        replace: true,
        state: {
          from: '/checkout',
          message: 'Please sign in to complete your order.',
        },
      })
    }
  }, [authLoading, isAuthenticated, userId, navigate])

  useEffect(() => {
    if (!user) return
    const nameParts = String(user.name || '').trim().split(/\s+/).filter(Boolean)
    setForm((prev) => ({
      ...prev,
      firstName: prev.firstName || nameParts[0] || '',
      lastName: prev.lastName || nameParts.slice(1).join(' ') || '',
      email: prev.email || (user.email || '').toLowerCase(),
      phone: prev.phone || user.phone || user.phoneNumber || '',
    }))
  }, [user])

  useEffect(() => {
    if (!toast) return undefined
    const timer = setTimeout(() => setToast(null), 3000)
    return () => clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    if (payState || showSuccess) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [payState, showSuccess])

  // Keep native checkout selects — theme niceSelect() breaks styling
  useEffect(() => {
    const restoreSelects = () => {
      document.querySelectorAll('.checkout-page select.checkout-select').forEach((select) => {
        const $ = window.jQuery || window.$
        if ($ && $.fn && typeof $.fn.niceSelect === 'function') {
          const $select = $(select)
          if ($select.next('.nice-select').length) {
            $select.niceSelect('destroy')
          }
        }
        select.style.display = 'block'
      })
      document.querySelectorAll('.checkout-page .nice-select').forEach((el) => el.remove())
    }

    restoreSelects()
    const timer = setInterval(restoreSelects, 400)
    const stop = setTimeout(() => clearInterval(timer), 4000)
    return () => {
      clearInterval(timer)
      clearTimeout(stop)
    }
  }, [deliveryMethod, form.province, step])

  const displayItems = cartItems.length > 0 ? cartItems : orderSnapshot
  const subtotal = pendingOrder?.subtotal ?? getTotalPrice()
  const shipping = pendingOrder?.shippingFee ?? 0
  const total = pendingOrder?.total ?? subtotal + shipping
  const itemCount = displayItems.reduce((sum, item) => sum + (item.quantity || 0), 0)
  const totalRwfLabel = `RWF ${Math.round(Number(total) || 0).toLocaleString('en-US')}`

  const districtOptions = form.province ? RWANDA_LOCATIONS[form.province] || [] : []

  const updateField = (key, value) => {
    setForm((prev) => {
      if (key === 'province') {
        return { ...prev, province: value, district: '' }
      }
      return { ...prev, [key]: value }
    })
    setErrors((prev) => {
      const next = { ...prev }
      delete next[key]
      if (key === 'province') delete next.district
      return next
    })
  }

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
  const isValidPhone = (value) => {
    const digits = value.replace(/\D/g, '')
    return digits.length >= 9 && digits.length <= 13
  }

  const validatePayment = () => {
    const next = {}
    if (!paymentMethod) next.paymentMethod = 'Select a payment method'
    else if (!isMethodAvailable(paymentMethod)) next.paymentMethod = 'This payment method is not available right now'
    if (selectedOption.kind === 'momo') {
      if (!payPhone.trim()) next.payPhone = `Enter your ${selectedOption.label} number`
      else if (!isValidPhone(payPhone)) next.payPhone = 'Enter a valid phone number, e.g. 0788 123 456'
    }
    return next
  }

  const validateStep = (targetStep) => {
    const next = {}

    if (targetStep === 1) {
      if (!form.firstName.trim()) next.firstName = 'First name is required'
      else if (form.firstName.trim().length < 2) next.firstName = 'Enter at least 2 characters'

      if (!form.lastName.trim()) next.lastName = 'Last name is required'
      else if (form.lastName.trim().length < 2) next.lastName = 'Enter at least 2 characters'

      if (!form.phone.trim()) next.phone = 'Phone number is required'
      else if (!isValidPhone(form.phone)) next.phone = 'Enter a valid phone number'

      if (!form.email.trim()) next.email = 'Email is required'
      else if (!isValidEmail(form.email.toLowerCase())) next.email = 'Enter a valid email address'
    }

    if (targetStep === 2) {
      if (!form.province.trim()) next.province = 'Province is required'
      if (!form.district.trim()) next.district = 'District is required'
      if (deliveryMethod === 'delivery') {
        if (!form.sector.trim()) next.sector = 'Sector / Cell is required'
        if (!form.street.trim()) next.street = 'Street / landmark is required'
        else if (form.street.trim().length < 5) next.street = 'Enter a more complete address'
        if (form.deliveryDate) {
          const selected = new Date(`${form.deliveryDate}T00:00:00`)
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          if (selected < today) next.deliveryDate = 'Delivery date cannot be in the past'
        }
      }
    }

    if (targetStep === 3) {
      Object.assign(next, validatePayment())
    }

    if (targetStep === 4) {
      if (!acceptTerms) next.terms = 'Please accept the terms to continue'
    }

    setErrors(next)
    return Object.keys(next).length === 0
  }

  const validateForm = () => {
    const next = {}

    if (!form.firstName.trim()) next.firstName = 'First name is required'
    else if (form.firstName.trim().length < 2) next.firstName = 'Enter at least 2 characters'

    if (!form.lastName.trim()) next.lastName = 'Last name is required'
    else if (form.lastName.trim().length < 2) next.lastName = 'Enter at least 2 characters'

    if (!form.phone.trim()) next.phone = 'Phone number is required'
    else if (!isValidPhone(form.phone)) next.phone = 'Enter a valid phone number'

    if (!form.email.trim()) next.email = 'Email is required'
    else if (!isValidEmail(form.email.toLowerCase())) next.email = 'Enter a valid email address'

    if (deliveryMethod === 'delivery') {
      if (!form.province.trim()) next.province = 'Province is required'
      if (!form.district.trim()) next.district = 'District is required'
      if (!form.sector.trim()) next.sector = 'Sector / Cell is required'
      if (!form.street.trim()) next.street = 'Street / landmark is required'
      else if (form.street.trim().length < 5) next.street = 'Enter a more complete address'
      if (form.deliveryDate) {
        const selected = new Date(`${form.deliveryDate}T00:00:00`)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        if (selected < today) next.deliveryDate = 'Delivery date cannot be in the past'
      }
    } else {
      if (!form.province.trim()) next.province = 'Province is required'
      if (!form.district.trim()) next.district = 'District is required'
    }

    Object.assign(next, validatePayment())

    if (!acceptTerms) next.terms = 'Please accept the terms to continue'

    setErrors(next)

    if (Object.keys(next).length === 0) return true

    if (next.firstName || next.lastName || next.phone || next.email) setStep(1)
    else if (next.province || next.district || next.sector || next.street || next.deliveryDate) setStep(2)
    else if (next.paymentMethod || next.payPhone) setStep(3)
    else setStep(LAST_STEP)

    return false
  }

  const goToStep = (nextStep) => {
    if (nextStep < 1 || nextStep > LAST_STEP) return
    if (nextStep > step) {
      for (let s = step; s < nextStep; s += 1) {
        if (!validateStep(s)) {
          setToast('Please fix the highlighted fields')
          setStep(s)
          return
        }
      }
    }
    setErrors({})
    setStep(nextStep)
  }

  const handleContinue = () => {
    if (!validateStep(step)) {
      setToast('Please fix the highlighted fields')
      return
    }
    setErrors({})
    setStep((prev) => Math.min(LAST_STEP, prev + 1))
  }

  const buildShippingAddress = () => {
    if (deliveryMethod === 'store') {
      return `Store pickup — ${STORE_PICKUP.address}`
    }
    return [
      form.street.trim(),
      form.sector.trim(),
      form.district.trim(),
      form.province.trim(),
      form.country.trim(),
    ]
      .filter(Boolean)
      .join(', ')
  }

  const buildOrderNotes = () => {
    const parts = []
    if (form.deliveryDate) parts.push(`Delivery date: ${form.deliveryDate}`)
    if (form.deliveryTime) parts.push(`Delivery time: ${form.deliveryTime}`)
    if (deliveryMethod === 'store') parts.push(`Pickup at ${STORE_PICKUP.name}`)
    if (form.province) parts.push(`Province: ${form.province}`)
    if (form.district) parts.push(`District: ${form.district}`)
    return parts.join(' | ')
  }

  const finishSuccess = () => {
    setShowSuccess(false)
    setPayState(null)
    setPendingOrder(null)
    setOrderSnapshot([])
    setToast('Order placed successfully!')
    navigate('/')
  }

  const viewOrders = () => {
    const orderId = pendingOrder?.id
    finishSuccess()
    navigate(orderId ? `${SITE_LINKS.orders}?order=${orderId}` : SITE_LINKS.orders)
  }

  const stopWaiting = () => {
    pollRef.current?.abort()
    pollRef.current = null
  }

  /** Wait for the customer to approve the mobile money prompt. */
  const waitForApproval = async (payment) => {
    stopWaiting()
    const controller = new AbortController()
    pollRef.current = controller
    setPayState({ phase: 'waiting', payment })

    const result = await waitForPayment(payment.id, { signal: controller.signal })
    if (controller.signal.aborted) return

    const latest = result?.payment || payment
    if (latest.status === 'paid') {
      setPayState(null)
      setShowSuccess(true)
    } else if (latest.status === 'failed') {
      setPayState({
        phase: 'failed',
        payment: latest,
        error: latest.errorMessage || 'The payment was declined or cancelled.',
      })
    } else {
      setPayState({ phase: 'timeout', payment: latest })
    }
  }

  /** Start (or retry) the online payment for an order that already exists. */
  const startPayment = async (order) => {
    stopWaiting()
    setPayState({ phase: 'starting' })
    try {
      const result = await initiatePayment(order.id, {
        method: paymentMethod,
        phone: selectedOption.kind === 'momo' ? payPhone.trim() : form.phone.trim(),
      })

      if (result.nextAction === 'redirect' && result.redirectUrl) {
        rememberPendingPayment({ paymentId: result.payment.id, orderId: order.id, method: paymentMethod })
        setPayState({ phase: 'redirecting' })
        window.location.assign(result.redirectUrl)
        return
      }

      await waitForApproval(result.payment)
    } catch (error) {
      if (error.status === 409) {
        // The order was already paid (e.g. a payment completed in another tab)
        setPayState(null)
        setShowSuccess(true)
        return
      }
      setPayState({ phase: 'failed', error: error.message || 'Could not start the payment. Please try again.' })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isAuthenticated || !userId) {
      navigate('/login', {
        replace: true,
        state: { from: '/checkout', message: 'Please sign in to complete your order.' },
      })
      return
    }
    if (cartItems.length === 0 && !pendingOrder) {
      setToast('Your cart is empty')
      navigate('/cart')
      return
    }
    if (!validateForm()) {
      setToast('Please fix the highlighted fields')
      return
    }

    setSubmitting(true)
    try {
      let order = pendingOrder
      if (!order) {
        // Create the order once; a failed payment can be retried on the same order
        await syncCartForCheckout()
        order = await createOrder(
          userId,
          {
            shippingAddress: buildShippingAddress(),
            phone: form.phone.trim(),
            paymentMethod: selectedOption.kind === 'card' ? 'Card' : 'Mobile Money',
            momoProvider: selectedOption.kind === 'momo' ? paymentMethod : undefined,
            deliveryMethod,
            customerName: `${form.firstName} ${form.lastName}`.trim(),
            customerEmail: form.email.trim().toLowerCase(),
            notes: buildOrderNotes(),
            shippingFee: shipping,
          },
          token || null
        )
        setPendingOrder(order)
        setOrderSnapshot(cartItems.map((item) => ({ ...item })))
        clearLocalCart()
      }

      await startPayment(order)
    } catch (error) {
      console.error('Error placing order:', error)
      setToast(error.message || 'Failed to place your order. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const closePaymentDialog = () => {
    stopWaiting()
    setPayState(null)
  }

  const changePaymentMethod = () => {
    closePaymentDialog()
    setErrors({})
    setStep(3)
  }

  if (cartItems.length === 0 && !showSuccess && !pendingOrder) {
    return (
      <div className="checkout-page">
        <MainHeader cartCount={0} />
        <main className="co-main">
          <div className="co-container">
            <section className="co-empty">
              <span className="co-empty__art" aria-hidden="true">
                <i className="fa-solid fa-cart-shopping" />
              </span>
              <h1 className="co-empty__title">Nothing to check out yet</h1>
              <p className="co-empty__text">Your cart is empty. Add products from the shop, then come back here to place your order.</p>
              <Link to={SITE_LINKS.shop} className="checkout-btn checkout-btn--primary">
                Browse the shop
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </Link>
            </section>
          </div>
        </main>
        <HomeFooter />
      </div>
    )
  }

  const fullName = `${form.firstName} ${form.lastName}`.trim() || user?.name || 'Customer'
  const paymentLabel = selectedOption.label
  // Card payments are charged in USD; show the approximate amount when the rate is known
  const cardAmountLabel = cardRate
    ? `USD ${(Math.ceil((Number(total) / cardRate) * 100) / 100).toFixed(2)}`
    : null
  const payButtonLabel = pendingOrder ? `Pay ${totalRwfLabel}` : `Place order & pay · ${formatPrice(total)}`
  const noMethodsAvailable = Array.isArray(availableMethods) && availableMethods.length === 0
  const currentStep = CHECKOUT_STEPS.find((item) => item.id === step) || CHECKOUT_STEPS[0]

  /** Section heading shared by every step. */
  const StepHead = ({ icon, title, text }) => (
    <header className="co-step-head">
      <span className="co-step-head__icon" aria-hidden="true">
        <i className={`fa-solid ${icon}`} />
      </span>
      <div>
        <span className="co-step-head__count">
          Step {step} of {CHECKOUT_STEPS.length}
        </span>
        <h2>{title}</h2>
        {text ? <p>{text}</p> : null}
      </div>
    </header>
  )

  return (
    <div className="checkout-page">
      <MainHeader cartCount={itemCount} />

      <main className="co-main">
        <div className="co-container">
          <section className="co-hero">
            <div className="co-hero__copy">
              <nav className="co-crumbs" aria-label="Breadcrumb">
                <Link to={SITE_LINKS.home}>Home</Link>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                <Link to={SITE_LINKS.cart}>Cart</Link>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                <span aria-current="page">Checkout</span>
              </nav>
              <h1 className="co-hero__title">Secure Checkout</h1>
              <p className="co-hero__text">
                {currentStep.hint} Your order is confirmed as soon as your payment goes through.
              </p>
            </div>
            <span className="co-hero__badge">
              <i className="fa-solid fa-lock" aria-hidden="true" />
              Secure checkout
            </span>
          </section>

          <nav className="checkout-steps" aria-label="Checkout progress">
            {CHECKOUT_STEPS.map((item) => {
              const isActive = step === item.id
              const isDone = step > item.id
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`checkout-steps__item ${isActive ? 'is-active' : ''} ${isDone ? 'is-done' : ''}`}
                  onClick={() => goToStep(item.id)}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <span className="checkout-steps__num" aria-hidden="true">
                    {isDone ? <i className="fa-solid fa-check" /> : <i className={`fa-solid ${item.icon}`} />}
                  </span>
                  <span className="checkout-steps__label">
                    <small>Step {item.id}</small>
                    {item.label}
                  </span>
                </button>
              )
            })}
          </nav>

          <div className="co-layout">
            <form
              className="checkout-form"
              id="checkout-form"
              onSubmit={(e) => {
                if (step < LAST_STEP) {
                  e.preventDefault()
                  handleContinue()
                  return
                }
                handleSubmit(e)
              }}
            >
              {step === 1 && (
                <section className="co-card">
                  <StepHead icon="fa-user" title="Contact information" text="We’ll use these details to confirm your order." />

                  {isAuthenticated && user ? (
                    <div className="checkout-signed-in">
                      <span className="checkout-signed-in__avatar" aria-hidden="true">
                        {(user.name || fullName).trim().charAt(0).toUpperCase() || <i className="fa-solid fa-user" />}
                      </span>
                      <div>
                        <strong>{user.name || fullName}</strong>
                        <p>Signed in · details pre-filled from your account</p>
                      </div>
                      <i className="fa-solid fa-circle-check checkout-signed-in__check" aria-hidden="true" />
                    </div>
                  ) : null}

                  <div className="checkout-grid">
                    <label className={`checkout-field ${errors.firstName ? 'has-error' : ''}`}>
                      <span>First name</span>
                      <input
                        type="text"
                        value={form.firstName}
                        onChange={(e) => updateField('firstName', e.target.value)}
                        placeholder="First name"
                        autoComplete="given-name"
                        aria-invalid={!!errors.firstName}
                      />
                      {errors.firstName ? <em className="checkout-field__error">{errors.firstName}</em> : null}
                    </label>
                    <label className={`checkout-field ${errors.lastName ? 'has-error' : ''}`}>
                      <span>Last name</span>
                      <input
                        type="text"
                        value={form.lastName}
                        onChange={(e) => updateField('lastName', e.target.value)}
                        placeholder="Last name"
                        autoComplete="family-name"
                        aria-invalid={!!errors.lastName}
                      />
                      {errors.lastName ? <em className="checkout-field__error">{errors.lastName}</em> : null}
                    </label>
                    <label className={`checkout-field ${errors.phone ? 'has-error' : ''}`}>
                      <span>Phone</span>
                      <div className="checkout-field__phone">
                        <span className="checkout-field__flag" aria-hidden="true">
                          🇷🇼
                        </span>
                        <input
                          type="tel"
                          value={form.phone}
                          onChange={(e) => updateField('phone', e.target.value)}
                          placeholder="+250 788 000 000"
                          autoComplete="tel"
                          aria-invalid={!!errors.phone}
                        />
                      </div>
                      {errors.phone ? <em className="checkout-field__error">{errors.phone}</em> : null}
                    </label>
                    <label className={`checkout-field ${errors.email ? 'has-error' : ''}`}>
                      <span>E-mail</span>
                      <input
                        type="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        autoComplete="email"
                        value={form.email}
                        onChange={(e) => updateField('email', e.target.value.toLowerCase())}
                        placeholder="name@example.com"
                        aria-invalid={!!errors.email}
                        style={{ textTransform: 'lowercase' }}
                      />
                      {errors.email ? <em className="checkout-field__error">{errors.email}</em> : null}
                    </label>
                  </div>
                </section>
              )}

              {step === 2 && (
                <section className="co-card">
                  <StepHead icon="fa-truck-fast" title="Delivery" text="Tell us where you are, then choose pickup or home delivery." />

                  <div className="co-block">
                    <h3 className="co-block__title">
                      <span>1</span>
                      Your location
                    </h3>
                    <div className="checkout-grid">
                      <label className={`checkout-field ${errors.province ? 'has-error' : ''}`}>
                        <span>Province</span>
                        <div className="checkout-select-wrap">
                          <select
                            className="checkout-select"
                            value={form.province}
                            onChange={(e) => updateField('province', e.target.value)}
                            aria-invalid={!!errors.province}
                          >
                            <option value="">Select province…</option>
                            {RWANDA_PROVINCES.map((province) => (
                              <option key={province} value={province}>
                                {province}
                              </option>
                            ))}
                          </select>
                          <i className="fa-solid fa-chevron-down" aria-hidden="true" />
                        </div>
                        {errors.province ? <em className="checkout-field__error">{errors.province}</em> : null}
                      </label>

                      <label className={`checkout-field ${errors.district ? 'has-error' : ''}`}>
                        <span>District</span>
                        <div className={`checkout-select-wrap ${!form.province ? 'is-disabled' : ''}`}>
                          <select
                            className="checkout-select"
                            value={form.district}
                            onChange={(e) => updateField('district', e.target.value)}
                            disabled={!form.province}
                            aria-invalid={!!errors.district}
                          >
                            <option value="">{form.province ? 'Select district…' : 'Select province first…'}</option>
                            {districtOptions.map((district) => (
                              <option key={district} value={district}>
                                {district}
                              </option>
                            ))}
                          </select>
                          <i className="fa-solid fa-chevron-down" aria-hidden="true" />
                        </div>
                        {errors.district ? <em className="checkout-field__error">{errors.district}</em> : null}
                      </label>

                      <label className={`checkout-field ${errors.sector ? 'has-error' : ''}`}>
                        <span>
                          Sector / Cell {deliveryMethod === 'store' ? <small>(optional)</small> : null}
                        </span>
                        <input
                          type="text"
                          value={form.sector}
                          onChange={(e) => updateField('sector', e.target.value)}
                          placeholder="Kimironko / Bibare"
                          aria-invalid={!!errors.sector}
                        />
                        {errors.sector ? <em className="checkout-field__error">{errors.sector}</em> : null}
                      </label>

                      <label className={`checkout-field ${errors.street ? 'has-error' : ''}`}>
                        <span>
                          Street / landmark {deliveryMethod === 'store' ? <small>(optional)</small> : null}
                        </span>
                        <input
                          type="text"
                          value={form.street}
                          onChange={(e) => updateField('street', e.target.value)}
                          placeholder="Street, building, or landmark"
                          autoComplete="street-address"
                          aria-invalid={!!errors.street}
                        />
                        {errors.street ? <em className="checkout-field__error">{errors.street}</em> : null}
                      </label>
                    </div>
                  </div>

                  <div className="co-block">
                    <h3 className="co-block__title">
                      <span>2</span>
                      How do you want to receive your order?
                    </h3>

                    <div className="fulfill-options" role="radiogroup" aria-label="Receive method">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={deliveryMethod === 'store'}
                        className={`fulfill-option ${deliveryMethod === 'store' ? 'is-active' : ''}`}
                        onClick={() => setDeliveryMethod('store')}
                      >
                        <span className="fulfill-option__radio" aria-hidden="true" />
                        <span className="fulfill-option__icon" aria-hidden="true">
                          <i className="fa-solid fa-store" />
                        </span>
                        <span className="fulfill-option__body">
                          <strong>Pickup at our store</strong>
                          <small>{STORE_PICKUP.address}</small>
                        </span>
                        <span className="fulfill-option__price">Free</span>
                      </button>

                      <button
                        type="button"
                        role="radio"
                        aria-checked={deliveryMethod === 'delivery'}
                        className={`fulfill-option ${deliveryMethod === 'delivery' ? 'is-active' : ''} ${
                          !form.province ? 'is-disabled' : ''
                        }`}
                        onClick={() => form.province && setDeliveryMethod('delivery')}
                        disabled={!form.province}
                      >
                        <span className="fulfill-option__radio" aria-hidden="true" />
                        <span className="fulfill-option__icon" aria-hidden="true">
                          <i className="fa-solid fa-truck" />
                        </span>
                        <span className="fulfill-option__body">
                          <strong>Home delivery</strong>
                          <small>
                            {form.province
                              ? `Deliver to ${form.district || form.province}, Rwanda`
                              : 'Select your province to unlock delivery'}
                          </small>
                        </span>
                        <span className="fulfill-option__price">Free</span>
                      </button>
                    </div>

                    {deliveryMethod === 'delivery' && form.province ? (
                      <div className="checkout-grid co-block__extra">
                        <label className={`checkout-field ${errors.deliveryDate ? 'has-error' : ''}`}>
                          <span>
                            Preferred date <small>(optional)</small>
                          </span>
                          <input
                            type="date"
                            value={form.deliveryDate}
                            onChange={(e) => updateField('deliveryDate', e.target.value)}
                            aria-invalid={!!errors.deliveryDate}
                          />
                          {errors.deliveryDate ? <em className="checkout-field__error">{errors.deliveryDate}</em> : null}
                        </label>
                        <label className="checkout-field">
                          <span>
                            Preferred time <small>(optional)</small>
                          </span>
                          <input type="time" value={form.deliveryTime} onChange={(e) => updateField('deliveryTime', e.target.value)} />
                        </label>
                      </div>
                    ) : null}

                    {deliveryMethod === 'store' ? (
                      <div className="fulfill-store-card">
                        <span className="fulfill-store-card__icon" aria-hidden="true">
                          <i className="fa-solid fa-location-dot" />
                        </span>
                        <div>
                          <strong>{STORE_PICKUP.name}</strong>
                          <p>{STORE_PICKUP.address}</p>
                          <p>
                            <a href={`tel:${STORE_PICKUP.phone.replace(/\s/g, '')}`}>{STORE_PICKUP.phone}</a> · we’ll call you when your
                            order is ready.
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </section>
              )}

              {step === 3 && (
                <section className="co-card">
                  <StepHead
                    icon="fa-credit-card"
                    title="Payment method"
                    text="Choose how you want to pay. You'll pay right after placing the order."
                  />

                  {noMethodsAvailable ? (
                    <p className="pay-unavailable" role="alert">
                      <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
                      <span>
                        Online payment is temporarily unavailable. Please try again later or call{' '}
                        <a href={CONTACT_INFO.phoneHref}>{CONTACT_INFO.phone}</a>.
                      </span>
                    </p>
                  ) : null}

                  <div className="pay-method-list" role="radiogroup" aria-label="Payment method">
                    {PAYMENT_OPTIONS.map((option) => {
                      const available = isMethodAvailable(option.id)
                      return (
                        <button
                          key={option.id}
                          type="button"
                          role="radio"
                          aria-checked={paymentMethod === option.id}
                          disabled={!available}
                          className={`pay-method ${paymentMethod === option.id ? 'is-active' : ''} ${
                            available ? '' : 'is-disabled'
                          }`}
                          onClick={() => {
                            setPaymentMethod(option.id)
                            setErrors((prev) => {
                              const next = { ...prev }
                              delete next.paymentMethod
                              delete next.payPhone
                              return next
                            })
                          }}
                        >
                          <span className="pay-method__radio" aria-hidden="true" />
                          <span className={`pay-method__icon ${option.logoContain ? 'pay-method__icon--contain' : ''}`} aria-hidden="true">
                            {option.logo ? <img src={option.logo} alt="" /> : <i className={`fa-solid ${option.icon}`} />}
                          </span>
                          <span className="pay-method__body">
                            <strong>{option.label}</strong>
                            <small>{available ? option.subtitle : 'Not available right now'}</small>
                          </span>
                          {option.popular && available ? <span className="pay-method__badge">Popular</span> : null}
                        </button>
                      )
                    })}
                  </div>

                  {selectedOption.kind === 'momo' ? (
                    <div className="pay-details">
                      <label className={`checkout-field ${errors.payPhone ? 'has-error' : ''}`}>
                        <span>{selectedOption.label} number</span>
                        <div className="checkout-field__phone">
                          <span className="checkout-field__flag" aria-hidden="true">
                            🇷🇼
                          </span>
                          <input
                            type="tel"
                            value={payPhone}
                            onChange={(e) => {
                              setPayPhoneInput(e.target.value)
                              setErrors((prev) => {
                                if (!prev.payPhone) return prev
                                const next = { ...prev }
                                delete next.payPhone
                                return next
                              })
                            }}
                            placeholder="0788 123 456"
                            autoComplete="tel"
                            aria-invalid={!!errors.payPhone}
                          />
                        </div>
                        {errors.payPhone ? <em className="checkout-field__error">{errors.payPhone}</em> : null}
                      </label>
                      <ol className="pay-steps">
                        <li>Place your order on the next step.</li>
                        <li>
                          You&apos;ll receive a {selectedOption.label} prompt on this number for <strong>{totalRwfLabel}</strong>.
                        </li>
                        <li>Enter your PIN to approve. Your order is confirmed automatically.</li>
                      </ol>
                    </div>
                  ) : (
                    <div className="pay-details">
                      <ol className="pay-steps">
                        <li>Place your order on the next step.</li>
                        <li>You&apos;ll be taken to a secure card page to enter your card details.</li>
                        <li>
                          Card payments are charged in US dollars
                          {cardAmountLabel ? (
                            <>
                              {' '}
                              (about <strong>{cardAmountLabel}</strong> for {totalRwfLabel})
                            </>
                          ) : null}
                          . Your bank may add a currency conversion fee.
                        </li>
                      </ol>
                      <p className="pay-details__note">
                        <i className="fa-solid fa-lock" aria-hidden="true" />
                        <span>Your card details are entered on the payment provider&apos;s page — Fabritech never sees them.</span>
                      </p>
                    </div>
                  )}

                  {errors.paymentMethod ? <em className="checkout-field__error">{errors.paymentMethod}</em> : null}
                </section>
              )}

              {step === 4 && (
                <section className="co-card">
                  <StepHead icon="fa-clipboard-check" title="Review your order" text="Check the details below, then place your order and pay." />
                  <div className="checkout-review">
                    {[
                      {
                        key: 'contact',
                        icon: 'fa-user',
                        title: 'Contact',
                        target: 1,
                        lines: [fullName, form.email, form.phone],
                      },
                      {
                        key: 'delivery',
                        icon: 'fa-truck-fast',
                        title: 'Delivery',
                        target: 2,
                        lines: [
                          deliveryMethod === 'store' ? `Pickup — ${STORE_PICKUP.name}` : 'Home delivery',
                          deliveryMethod === 'store' ? STORE_PICKUP.address : buildShippingAddress(),
                          form.province && deliveryMethod === 'store'
                            ? [form.district, form.province, form.country].filter(Boolean).join(', ')
                            : null,
                          form.deliveryDate ? `Date: ${form.deliveryDate}` : null,
                          form.deliveryTime ? `Time: ${form.deliveryTime}` : null,
                        ],
                      },
                      {
                        key: 'payment',
                        icon: 'fa-credit-card',
                        title: 'Payment',
                        target: 3,
                        lines: [
                          paymentLabel,
                          selectedOption.kind === 'momo'
                            ? `Prompt sent to ${payPhone}`
                            : `Secure card page${cardAmountLabel ? ` · about ${cardAmountLabel}` : ''}`,
                        ],
                      },
                    ].map((block) => (
                      <div key={block.key} className="checkout-review__block">
                        <div className="checkout-review__head">
                          <h3>
                            <i className={`fa-solid ${block.icon}`} aria-hidden="true" />
                            {block.title}
                          </h3>
                          <button type="button" onClick={() => setStep(block.target)}>
                            <i className="fa-solid fa-pen" aria-hidden="true" />
                            Edit
                          </button>
                        </div>
                        {block.lines.filter(Boolean).map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                      </div>
                    ))}
                  </div>

                  <label className={`checkout-terms ${errors.terms ? 'has-error' : ''}`}>
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => {
                        setAcceptTerms(e.target.checked)
                        setErrors((prev) => {
                          if (!prev.terms) return prev
                          const next = { ...prev }
                          delete next.terms
                          return next
                        })
                      }}
                    />
                    <span className="checkout-terms__box" aria-hidden="true">
                      <i className="fa-solid fa-check" />
                    </span>
                    <span className="checkout-terms__text">
                      I accept the{' '}
                      <a href="/about" target="_blank" rel="noreferrer">
                        terms of the user agreement
                      </a>
                    </span>
                  </label>
                  {errors.terms ? <em className="checkout-field__error">{errors.terms}</em> : null}
                </section>
              )}

              <div className="checkout-step-actions">
                {step > 1 ? (
                  <button
                    type="button"
                    className="checkout-btn checkout-btn--ghost"
                    onClick={() => setStep((prev) => Math.max(1, prev - 1))}
                  >
                    <i className="fa-solid fa-arrow-left" aria-hidden="true" />
                    Back
                  </button>
                ) : (
                  <Link to={SITE_LINKS.cart} className="checkout-btn checkout-btn--ghost">
                    <i className="fa-solid fa-arrow-left" aria-hidden="true" />
                    Back to cart
                  </Link>
                )}
                {step < LAST_STEP ? (
                  <button type="button" className="checkout-btn checkout-btn--primary" onClick={handleContinue}>
                    Continue to {CHECKOUT_STEPS[step].label.toLowerCase()}
                    <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="checkout-btn checkout-btn--primary"
                    disabled={submitting || Boolean(payState) || noMethodsAvailable}
                  >
                    {submitting ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                        {pendingOrder ? 'Starting payment…' : 'Placing order…'}
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-lock" aria-hidden="true" />
                        {payButtonLabel}
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>

            {/* Order summary */}
            <aside className="checkout-order" aria-labelledby="checkout-order-title">
              <div className="checkout-order__card">
                <div className="checkout-order__head">
                  <h2 className="checkout-order__title" id="checkout-order-title">
                    Order summary
                  </h2>
                  <label className="checkout-currency" title="Display currency">
                    <i className="fa-solid fa-coins" aria-hidden="true" />
                    <select value={currency} onChange={(e) => setCurrency(e.target.value)} aria-label="Display currency">
                      {currencies.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <ul className="checkout-order__items">
                  {displayItems.map((item) => {
                    const name = item.name || item.title || 'Product'
                    const unit = item.price || 0
                    return (
                      <li key={item.id} className="checkout-order__item">
                        <span className="checkout-order__thumb">
                          <img
                            src={item.image || item.img || '/placeholder-image.png'}
                            alt=""
                            onError={(e) => {
                              e.target.onerror = null
                              e.target.src = '/placeholder-image.png'
                            }}
                          />
                          <span className="checkout-order__qty" aria-label={`Quantity ${item.quantity}`}>
                            {item.quantity}
                          </span>
                        </span>
                        <p className="checkout-order__name">{name}</p>
                        <p className="checkout-order__price">{formatPrice(unit * item.quantity)}</p>
                      </li>
                    )
                  })}
                </ul>

                <dl className="checkout-order__breakdown">
                  <div className="checkout-order__row">
                    <dt>
                      Subtotal ({itemCount} item{itemCount === 1 ? '' : 's'})
                    </dt>
                    <dd>{formatPrice(subtotal)}</dd>
                  </div>
                  <div className="checkout-order__row">
                    <dt>Delivery</dt>
                    <dd className="checkout-order__free">Free</dd>
                  </div>
                </dl>

                <div className="checkout-order__total">
                  <span>Total</span>
                  <strong>{formatPrice(total)}</strong>
                </div>
                {isForeign ? (
                  <p className="checkout-currency-note" role="note">
                    <i className="fa-solid fa-circle-info" aria-hidden="true" />
                    <span>
                      Shown in <strong>{currency}</strong>. You pay <strong>{totalRwfLabel}</strong> ({baseCurrency}).
                    </span>
                  </p>
                ) : null}

                <div className="checkout-order__pay">
                  <span>We accept</span>
                  <ul>
                    <li>
                      <img src="/assets/img/brands/MTN-logo.jpg" alt="MTN Mobile Money" title="MTN Mobile Money" />
                    </li>
                    <li>
                      <img src="/assets/img/brands/Airtel-logo.png" alt="Airtel Money" title="Airtel Money" />
                    </li>
                    <li title="Visa / Mastercard">
                      <i className="fa-solid fa-credit-card" aria-hidden="true" />
                      <span className="visually-hidden">Visa / Mastercard</span>
                    </li>
                  </ul>
                </div>
              </div>

              <ul className="checkout-assure">
                <li>
                  <i className="fa-solid fa-shield-halved" aria-hidden="true" />
                  <span>
                    <strong>Secure payments</strong>
                    Approve on your phone or pay on a secure card page
                  </span>
                </li>
                <li>
                  <i className="fa-solid fa-headset" aria-hidden="true" />
                  <span>
                    <strong>Need help?</strong>
                    Call <a href={CONTACT_INFO.phoneHref}>{CONTACT_INFO.phone}</a>
                  </span>
                </li>
              </ul>
            </aside>
          </div>
        </div>
      </main>

      <HomeFooter />

      {toast && (
        <div className="checkout-toast" role="status">
          <i className="fa-solid fa-circle-info" aria-hidden="true" />
          {toast}
        </div>
      )}

      {payState && (
        <div className="checkout-modal-overlay" role="presentation">
          <div
            className="checkout-modal checkout-modal--center checkout-paying"
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-paying-title"
            aria-live="polite"
          >
            {['waiting', 'failed', 'timeout'].includes(payState.phase) ? (
              <button type="button" className="checkout-modal__close" onClick={closePaymentDialog} aria-label="Close">
                <i className="fa-solid fa-times" />
              </button>
            ) : null}

            {payState.phase === 'starting' || payState.phase === 'redirecting' ? (
              <>
                <div className="checkout-paying__icon" aria-hidden="true">
                  <i className="fa-solid fa-spinner fa-spin" />
                </div>
                <h2 id="checkout-paying-title">
                  {payState.phase === 'redirecting' ? 'Opening the secure card page…' : 'Starting your payment…'}
                </h2>
                <p className="checkout-modal__lead">
                  {payState.phase === 'redirecting'
                    ? 'You will be redirected to enter your card details.'
                    : 'Please wait a moment and keep this page open.'}
                </p>
              </>
            ) : null}

            {payState.phase === 'waiting' ? (
              <>
                <div className="checkout-paying__icon checkout-paying__icon--phone" aria-hidden="true">
                  <i className="fa-solid fa-mobile-screen-button" />
                </div>
                <h2 id="checkout-paying-title">Approve the payment on your phone</h2>
                <p className="checkout-modal__lead">
                  We sent a {selectedOption.label} request for <strong>{totalRwfLabel}</strong> to{' '}
                  <strong>{payState.payment?.phone || payPhone}</strong>. Enter your PIN to approve it.
                </p>
                <p className="checkout-paying__status">
                  <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />
                  Waiting for confirmation…
                </p>
                <p className="checkout-note">
                  No prompt? Check that the number is correct and has enough balance, then try again.
                </p>
                <div className="checkout-modal__actions">
                  <button type="button" className="checkout-btn checkout-btn--ghost" onClick={changePaymentMethod}>
                    Change payment method
                  </button>
                </div>
              </>
            ) : null}

            {payState.phase === 'failed' ? (
              <>
                <div className="checkout-paying__icon checkout-paying__icon--error" aria-hidden="true">
                  <i className="fa-solid fa-xmark" />
                </div>
                <h2 id="checkout-paying-title">Payment not completed</h2>
                <p className="checkout-modal__lead">{paymentErrorMessage(payState.error)}</p>
                {pendingOrder ? (
                  <p className="checkout-note">
                    Your order #{pendingOrder.id} is saved, so you won&apos;t need to fill in your details again.
                  </p>
                ) : null}
                <div className="checkout-modal__actions">
                  {pendingOrder ? (
                    <button type="button" className="checkout-btn checkout-btn--primary" onClick={() => startPayment(pendingOrder)}>
                      <i className="fa-solid fa-rotate-right" aria-hidden="true" />
                      Try again
                    </button>
                  ) : null}
                  <button type="button" className="checkout-btn checkout-btn--ghost" onClick={changePaymentMethod}>
                    Change payment method
                  </button>
                </div>
              </>
            ) : null}

            {payState.phase === 'timeout' ? (
              <>
                <div className="checkout-paying__icon checkout-paying__icon--warn" aria-hidden="true">
                  <i className="fa-solid fa-hourglass-half" />
                </div>
                <h2 id="checkout-paying-title">Still waiting for your approval</h2>
                <p className="checkout-modal__lead">
                  We haven&apos;t received a confirmation yet. If you already approved the payment, check again in a moment.
                </p>
                <div className="checkout-modal__actions">
                  <button
                    type="button"
                    className="checkout-btn checkout-btn--primary"
                    onClick={() => waitForApproval(payState.payment)}
                  >
                    <i className="fa-solid fa-rotate" aria-hidden="true" />
                    Check again
                  </button>
                  <button type="button" className="checkout-btn checkout-btn--ghost" onClick={() => startPayment(pendingOrder)}>
                    Send a new request
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {showSuccess && (
        <div className="checkout-modal-overlay" onClick={finishSuccess} role="presentation">
          <div
            className="checkout-modal checkout-modal--center"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="checkout-success-icon" aria-hidden="true">
              <i className="fa-solid fa-check" />
            </div>
            <h2>Payment received</h2>
            <p className="checkout-modal__lead">
              Thank you! Your order{pendingOrder?.id ? ` #${pendingOrder.id}` : ''} is paid and confirmed.
            </p>
            <p className="checkout-note">
              We&apos;ve emailed your order details. Our team will contact you shortly to arrange{' '}
              {deliveryMethod === 'store' ? 'pickup at our store' : 'delivery'}.
            </p>
            <button type="button" className="checkout-btn checkout-btn--primary" onClick={viewOrders}>
              <i className="fa-solid fa-box" aria-hidden="true" />
              View my orders
            </button>
            <button type="button" className="checkout-btn checkout-btn--ghost" onClick={finishSuccess}>
              Back to home
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Checkout
